// src/services/swipeTrackingService.ts
import { logger } from '../utils/logger';
// ✅ FIXED: Added comprehensive logging for debugging

import { supabase } from './supabase';

const DAILY_SWIPE_LIMIT = 25;

export interface SwipeStatus {
  swipesRemaining: number;
  totalSwipesToday: number;
  limitReached: boolean;
  isPremium: boolean;
}

/**
 * Get current swipe status for the user
 */
export async function getSwipeStatus(): Promise<SwipeStatus> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.log('⚠️ No user found for swipe status');
      return {
        swipesRemaining: 0,
        totalSwipesToday: 0,
        limitReached: true,
        isPremium: false,
      };
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('swipes_today, last_swipe_date, is_premium, premium_expires_at')
      .eq('id', user.id)
      .single();

    if (!profile) {
      logger.log('⚠️ No profile found for swipe status');
      return {
        swipesRemaining: DAILY_SWIPE_LIMIT,
        totalSwipesToday: 0,
        limitReached: false,
        isPremium: false,
      };
    }

    logger.log('📊 Profile swipe data:', {
      swipes_today: profile.swipes_today,
      last_swipe_date: profile.last_swipe_date,
      is_premium: profile.is_premium
    });

    // Check if premium and not expired
    const isPremium = profile.is_premium &&
      (!profile.premium_expires_at || new Date(profile.premium_expires_at) > new Date());

    // Premium users have unlimited swipes
    if (isPremium) {
      logger.log('✨ Premium user - unlimited swipes');
      return {
        swipesRemaining: -1, // -1 = unlimited
        totalSwipesToday: 0,
        limitReached: false,
        isPremium: true,
      };
    }

    // Check if swipe count needs to be reset (new day)
    const today = new Date().toISOString().split('T')[0];
    const lastSwipeDate = profile.last_swipe_date || '';

    let swipesToday = 0;
    if (lastSwipeDate === today) {
      swipesToday = profile.swipes_today || 0;
    } else {
      logger.log('🌅 New day detected, swipes will reset on next swipe');
    }

    const remaining = Math.max(0, DAILY_SWIPE_LIMIT - swipesToday);
    const limitReached = swipesToday >= DAILY_SWIPE_LIMIT;

    logger.log('📈 Swipe status:', {
      swipesToday,
      remaining,
      limitReached,
      today,
      lastSwipeDate
    });

    return {
      swipesRemaining: remaining,
      totalSwipesToday: swipesToday,
      limitReached,
      isPremium: false,
    };
  } catch (error) {
    logger.error('❌ Error getting swipe status:', error);
    return {
      swipesRemaining: 0,
      totalSwipesToday: 0,
      limitReached: true,
      isPremium: false,
    };
  }
}

/**
 * Record a swipe and check if limit is reached
 * Returns true if swipe was recorded, false if limit reached
 */
export async function recordSwipe(): Promise<{
  success: boolean;
  swipesRemaining: number;
  limitReached: boolean;
  isPremium: boolean;
}> {
  try {
    logger.log('🔄 Recording swipe...');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.log('❌ No user found for recording swipe');
      return { success: false, swipesRemaining: 0, limitReached: true, isPremium: false };
    }

    logger.log('👤 User ID:', user.id);

    // Use the database function for atomic increment
    logger.log('📡 Calling increment_swipe_count RPC...');
    const { data, error } = await supabase.rpc('increment_swipe_count', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('❌ Error calling increment_swipe_count:', error);
      logger.error('Error details:', JSON.stringify(error, null, 2));
      return { success: false, swipesRemaining: 0, limitReached: true, isPremium: false };
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      logger.error('❌ No data returned from increment_swipe_count');
      logger.log('Data received:', data);
      return { success: false, swipesRemaining: 0, limitReached: true, isPremium: false };
    }

    const result = data[0];
    logger.log('✅ RPC result:', result);

    // Premium users always succeed
    if (result.premium_status) {
      logger.log('✨ Premium user swipe recorded');
      return {
        success: true,
        swipesRemaining: -1, // unlimited
        limitReached: false,
        isPremium: true,
      };
    }

    // Check if limit was just reached
    const limitReached = result.limit_reached;
    const swipesRemaining = Math.max(0, DAILY_SWIPE_LIMIT - result.swipes_count);

    logger.log('📊 Free user swipe recorded:', {
      swipes_count: result.swipes_count,
      swipesRemaining,
      limitReached
    });

    return {
      success: !limitReached,
      swipesRemaining,
      limitReached,
      isPremium: false,
    };
  } catch (error) {
    logger.error('❌ Error in recordSwipe:', error);
    return { success: false, swipesRemaining: 0, limitReached: true, isPremium: false };
  }
}

/**
 * Check if user can swipe (without recording)
 */
export async function canSwipe(): Promise<boolean> {
  const status = await getSwipeStatus();
  return !status.limitReached;
}

/**
 * Reset swipe count manually (for testing/admin)
 */
export async function resetSwipeCount(): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        swipes_today: 0,
        last_swipe_date: null,
      })
      .eq('id', user.id);

    if (error) {
      logger.error('Error resetting swipe count:', error);
      return { success: false, error: error.message };
    }

    logger.log('✅ Swipe count reset successfully');
    return { success: true };
  } catch (error: any) {
    logger.error('Error in resetSwipeCount:', error);
    return { success: false, error: error.message };
  }
}